import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './TimezoneSettings.module.scss'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Item } from '@react-stately/collections'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useTimezones } from 'hooks/queries/useTimezones'
import { Key, useCallback, useEffect, useState } from 'react'

export const TimezoneSettings = () => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: timezones = [] } = useTimezones()
  const currentTimezone = brandPlan?.timezone ?? 'Etc/UTC'

  const findTimezoneName = useCallback((id: string) => timezones.find((tz) => tz.id === id)?.name ?? '', [timezones])

  const [inputValue, setInputValue] = useState('')
  const { mutate: updateBrandPlan } = useUpdateBrandPlan(
    { id: brandPlan?.id ?? 0 },
    {
      onSuccess: () => {
        showToastMessage('Timezone updated.')
      },
      onError: () => {
        showToastMessage("We couldn't update the timezone.", 'error')
      },
    },
  )

  // Keep input in sync when brand plan or timezones data loads/changes
  useEffect(() => {
    setInputValue(findTimezoneName(currentTimezone))
  }, [currentTimezone, findTimezoneName])

  const handleSelectionChange = (key: Key | null) => {
    if (!key || key === currentTimezone) return
    setInputValue(findTimezoneName(key as string))
    updateBrandPlan({ timezone: key as string })
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.headerAndDescription}>
        <Heading level={3}>Timezone</Heading>
        <Text variant="secondary" color="var(--dark-40)">
          Pick your preferred time zone for scheduling posts in. This will appear on the calendar and affect the
          recommended times.
        </Text>
      </div>
      <ComboBox
        aria-label="Timezone"
        selectedKey={currentTimezone}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={handleSelectionChange}
        defaultItems={timezones}
        isVirtualized={false}
        popoverMaxHeight={300}
      >
        {(item) => <Item key={item.id}>{item.name}</Item>}
      </ComboBox>
    </div>
  )
}
